/**
 * Authoritative Polymarket per-market tick size.
 *
 * The engine frame's `tickSize` comes from Gamma and goes stale: on the WC
 * final's "team to advance" market the frame said `"0.01"` while the CLOB's
 * own `GET /markets/{conditionId}` returned `minimum_tick_size: 0.0025`
 * (verified live 2026-07-18; the book was quoting 59.75 / 40.25, a
 * quarter-cent grid). Validating against the frame rejected a price that was
 * literally the resting bid.
 *
 * The CLOB is the only authority on its own grid, so the order path asks it
 * and treats the frame as a fetch-failure fallback — never a hardcoded 1¢.
 *
 * Ticks are NOT an enum. 0.0025 was outside the set anyone had assumed
 * ("0.1" | "0.01" | "0.001" | "0.0001"), so everything here handles an
 * arbitrary decimal, converted to the integer centi-cent domain
 * (0.0025 prob = 0.25¢ = 25 centi-cents) where grid math is exact.
 */

const HOST = 'https://clob.polymarket.com';

/** Ticks the CLOB SDK's ROUNDING_CONFIG actually keys on. This is a
 *  constraint of the client library, NOT of the venue — the venue happily
 *  runs a 0.0025 market. */
export const SDK_ROUNDING_TICKS = ['0.1', '0.01', '0.001', '0.0001'] as const;
export type SdkRoundingTick = (typeof SDK_ROUNDING_TICKS)[number];

/** conditionId → minimum_tick_size (as the CLOB spelled it), or null when
 *  the lookup failed. Nulls are cached too: a market whose tick we couldn't
 *  fetch shouldn't re-hit the network on every keystroke in the order form. */
const _cache = new Map<string, string | null>();
const _inflight = new Map<string, Promise<string | null>>();

/** Test seam. */
export function clearTickSizeCache(): void {
  _cache.clear();
  _inflight.clear();
}

/**
 * The market's real minimum tick, from the CLOB. Returns null on any
 * failure (offline, geoblock, unexpected shape) so the caller can fall back
 * to the frame rather than assert a grid that may not exist.
 *
 * Cached per conditionId, and concurrent callers share one request — the
 * order form asks on every render pass.
 */
export async function fetchMinTickSize(conditionId: string): Promise<string | null> {
  if (!conditionId) return null;
  const hit = _cache.get(conditionId);
  if (hit !== undefined) return hit;
  const pending = _inflight.get(conditionId);
  if (pending) return pending;

  const req = (async (): Promise<string | null> => {
    try {
      const res = await fetch(`${HOST}/markets/${conditionId}`);
      if (!res.ok) return null;
      const body = (await res.json()) as { minimum_tick_size?: unknown };
      const raw = body?.minimum_tick_size;
      if (raw == null) return null;
      const n = Number(raw);
      if (!Number.isFinite(n) || n <= 0 || n >= 1) return null;
      return String(raw);
    } catch {
      return null;
    } finally {
      _inflight.delete(conditionId);
    }
  })();

  _inflight.set(conditionId, req);
  const value = await req;
  _cache.set(conditionId, value);
  return value;
}

/**
 * Tick in CENTS for the price validator, authoritative-first.
 *
 * Returns null when neither source knows — deliberately distinguishable
 * from "1¢", so the caller picks its own policy instead of inheriting a
 * fabricated grid. (Silently assuming 1¢ is the bug this module exists to
 * fix.)
 */
export function resolveTickCents(
  authoritativeTick: string | null | undefined,
  frameTick: string | null | undefined,
): number | null {
  const pick = (raw: string | null | undefined): number | null => {
    if (raw == null || String(raw).trim() === '') return null;
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0 || n >= 1) return null;
    // prob → cents, but only when the authoritative grid is exactly
    // representable at the 0.01¢ grain used by every downstream price path.
    // Rounding a finer or incommensurate tick to zero would make the
    // cancel-first replacement validator treat the grid as unrestricted.
    const tickCcExact = n * 100 * 100;
    const tickCc = Math.round(tickCcExact);
    if (tickCc <= 0 || Math.abs(tickCcExact - tickCc) > 1e-6) return null;
    return tickCc / 100;
  };
  return pick(authoritativeTick) ?? pick(frameTick);
}

/**
 * Which SDK rounding grid to submit under.
 *
 * The SDK uses `tickSize` ONLY to choose how many decimals to round the
 * price to — `priceValid` merely range-checks and never verifies grid
 * alignment. So the requirement is simply: pick the finest supported grid
 * that CONTAINS the market's real grid, and the price survives untouched.
 * 0.0025 is 25 × 0.0001, so 0.0001 contains it exactly.
 *
 * Widening to a COARSER grid is the dangerous direction and is never done
 * here: submitting 0.4025 under "0.01" rounds it to 0.40 — a silent
 * 25-basis-point change to a real order. That was the prior behavior of
 * `normalizeClobTickSize`, which collapsed anything unrecognized to "0.01".
 *
 * The venue still enforces its own 0.0025 rule server-side; client-side
 * validation uses the REAL tick so the trader is told "multiple of 0.25¢"
 * up front instead of eating a rejection.
 */
export function sdkRoundingTickFor(minTick: string | null | undefined): SdkRoundingTick {
  if (minTick == null) return '0.01';
  const n = Number(minTick);
  if (!Number.isFinite(n) || n <= 0 || n >= 1) return '0.01';
  const exact = SDK_ROUNDING_TICKS.find((t) => Number(t) === n);
  if (exact) return exact;
  // Not a supported grid: use the finest one that contains it. Compared in
  // integer centi-cents so 0.0025 → 25 is exact rather than 0.24999….
  const finest = SDK_ROUNDING_TICKS[SDK_ROUNDING_TICKS.length - 1];
  return finest;
}
